import { useIntl } from 'react-intl'
import messages from './messages'
import { selectCurrentPatient } from '../../reducers/currentPatientReducer'
import { selectCurrentVisits } from '../../reducers/currentVisitsReducer'
import { useAppSelector } from '../../store/hooks'
import { getItemsWithSummaryReview } from '../../utils/schemaUtils'
import { useAccount } from '../../client/modules/account/AccountProvider'
import { useParams } from 'react-router-dom'

import ActionBar from '../../client/modules/common/components/ActionBar'
import Header from '../../client/modules/common/components/Header'
import EmptyBanner from '../EmptyBanner'
import VisitView from './components/VisitView'
import TimelineItem from './components/VisitDisplays/TimelineItem'
import RowItem from './components/VisitDisplays/RowItem/RowItem'
import GrowthChart from './components/GrowthChart'
import GrowthChartSummary from './components/GrowthChart/GrowthChartSummary'
import AISummary from './components/AISummary'
import DossierToolbar from './components/DossierToolbar'
import { SHOULD_SHOW_GROWTH_CHART } from './constants'
import VisitFormModal from '../../client/modules/patient/components/VisitFormModal'
import PatientModal from '../../client/modules/patient/components/PatientModal'
import PrescriptionModal from './components/Prescription/PrescriptionModal'

import {
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Flex,
  Spinner,
  Box,
  useDisclosure
} from '@chakra-ui/react'

import { usePatientData } from './hooks/usePatientData'
import { useVisitActions } from './hooks/useVisitActions'

export default function PatientDossier() {
  const { formatMessage } = useIntl()
  const {
    schema,
    isFreeMaxUsageReached,
    dossierPreferences,
    setDossierPreferences
  } = useAccount()
  const { patientId } = useParams()
  const {
    isOpen: isPatientDrawerOpen,
    onOpen: onPatientDrawerOpen,
    onClose: onPatientDrawerClose
  } = useDisclosure()

  const currentPatient = useAppSelector(selectCurrentPatient)
  const currentVisits = useAppSelector(selectCurrentVisits)
  const visitSchema = getItemsWithSummaryReview(schema.visit)

  const {
    displayMode,
    sortOrder,
    showCharts,
    toggleDisplayMode,
    toggleSortOrder,
    toggleShowCharts,
    currentVisit,
    followUpFlag,
    isVisitViewModalOpen,
    onVisitViewModalClose,
    isVisitFormModalOpen,
    onVisitFormModalClose,
    isPrescriptionModalOpen,
    onPrescriptionModalOpen,
    onPrescriptionModalClose,
    handleVisitSelect,
    handleVisitEdit,
    handleVisitDelete,
    handleAddVisit,
    handleAddFollowUp
  } = useVisitActions({
    preferences: dossierPreferences,
    onPreferencesChange: setDossierPreferences
  })

  const { visitsLoading, summary, summaryLoading, handleGenerateSummary } =
    usePatientData(patientId, sortOrder)

  return (
    <Box
      as="main"
      display="flex"
      flexDirection="column"
      position="relative"
      width="full"
      minH="calc(100vh - 48px)"
      p={{ base: 3, md: 4, '2xl': 5 }}
      textAlign="start"
      overflow="hidden"
    >
      <Flex
        flexDir={{ base: 'column', '2xl': 'row' }}
        justifyContent="space-between"
        align={{ base: 'stretch', '2xl': 'center' }}
        px={{ base: 0, md: 2, '2xl': 4 }}
        gap={{ base: 2, '2xl': 4 }}
        mt={{ base: 1, md: 2, '2xl': 4 }}
        mb={{ base: 3, '2xl': 4 }}
      >
        <Header>{formatMessage(messages.visitsAndFollowUps)}</Header>
        <DossierToolbar
          displayMode={displayMode}
          onToggleDisplayMode={toggleDisplayMode}
          sortOrder={sortOrder}
          onToggleSortOrder={toggleSortOrder}
          hasVisits={currentVisits.length > 0}
          showCharts={showCharts}
          onToggleCharts={toggleShowCharts}
          summaryLoading={summaryLoading}
          onGenerateSummary={handleGenerateSummary}
          onPrescriptionOpen={onPrescriptionModalOpen}
        />
      </Flex>

      {SHOULD_SHOW_GROWTH_CHART && !showCharts && currentVisits.length > 0 && (
        <GrowthChartSummary
          patient={currentPatient}
          visits={currentVisits}
          onOpenCharts={toggleShowCharts}
        />
      )}

      {showCharts && (
        <GrowthChart patient={currentPatient} visits={currentVisits} />
      )}

      {currentVisits.length === 0 && !visitsLoading && (
        <EmptyBanner isPatientSelected={true} />
      )}

      {visitsLoading && (
        <Flex justifyContent="center">
          <Spinner />
        </Flex>
      )}

      <VisitFormModal
        key={currentPatient.id}
        patientName={currentPatient.name}
        currentVisitValues={currentVisit}
        isEditMode={!!currentVisit}
        isFollowUp={followUpFlag}
        sortOrder={sortOrder}
        isOpen={isVisitFormModalOpen}
        onClose={onVisitFormModalClose}
      />

      <PatientModal
        historySchema={schema.history}
        isOpen={isPatientDrawerOpen}
        onClose={onPatientDrawerClose}
      />

      <VisitView
        values={currentVisit}
        isOpen={isVisitViewModalOpen}
        onClose={onVisitViewModalClose}
      />

      <PrescriptionModal
        isOpen={isPrescriptionModalOpen}
        onClose={onPrescriptionModalClose}
        patient={currentPatient}
        lastVisit={currentVisits.length > 0 ? currentVisits[0] : null}
      />

      <AISummary summary={summary} summaryLoading={summaryLoading} />

      {displayMode === 'TABLE' && currentVisits.length > 0 && (
        <TableContainer
          overflowX="auto"
          mx={{ base: 0, md: 4 }}
          my={4}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="12px"
          boxShadow="0 1px 2px rgba(15, 23, 42, 0.04)"
        >
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                {['Visit', ...visitSchema, 'Actions'].map((item) => {
                  const isActions = item === 'Actions'
                  return (
                    <Th
                      key={item}
                      px={4}
                      py={3}
                      bg="gray.50"
                      borderColor="gray.200"
                      color="gray.500"
                      fontSize="10px"
                      fontWeight="700"
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                      whiteSpace="nowrap"
                      position={isActions ? 'sticky' : 'static'}
                      insetInlineEnd={isActions ? 0 : undefined}
                      zIndex={isActions ? 2 : undefined}
                      textAlign={isActions ? 'end' : 'start'}
                    >
                      {item}
                    </Th>
                  )
                })}
              </Tr>
            </Thead>
            <Tbody>
              {currentVisits.map((visit) => (
                <RowItem
                  key={visit.id}
                  visit={visit}
                  birthDate={currentPatient.dateOfBirth}
                  onVisitSelect={handleVisitSelect}
                  onVisitEdit={handleVisitEdit}
                  onVisitDelete={handleVisitDelete}
                />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {displayMode === 'TIMELINE' && (
        <Flex
          flexDir="column"
          px={{ base: 0, md: 2, '2xl': 8 }}
          py={{ base: 2, md: 3, '2xl': 4 }}
        >
          {currentVisits.map((visit) => (
            <TimelineItem
              key={visit.id}
              visit={visit}
              birthDate={currentPatient.dateOfBirth}
              onVisitSelect={handleVisitSelect}
              onVisitEdit={handleVisitEdit}
              onVisitDelete={handleVisitDelete}
            />
          ))}
        </Flex>
      )}

      <Box
        position="fixed"
        bottom={{ base: 4, md: 6 }}
        left="50%"
        transform="translateX(-50%)"
        zIndex={30}
        display="flex"
        justifyContent="center"
        maxW="calc(100vw - 24px)"
        pointerEvents="none"
        sx={{ '& > *': { pointerEvents: 'auto' } }}
      >
        <ActionBar
          onAddPatientClick={onPatientDrawerOpen}
          onAddVisitClick={handleAddVisit}
          onAddFollowUpClick={handleAddFollowUp}
          isPatientSelected={currentPatient.id !== ''}
          disablePatientCreate={isFreeMaxUsageReached}
          disableVisitCreate={isFreeMaxUsageReached}
        />
      </Box>
    </Box>
  )
}
